import { cloneDeep, get, omit } from 'lodash';
import { hexToRgba } from '@meitu/saas-colors';
import { type IFramework } from '@/framework';
import { createDefaultVectorLayerTextInfo } from '@/layers/vector-layer/utils/text';
import { checkIsDarkColor } from '@/utils/color-kit';
import {
    extractBaseTextInfoFromVectorLayer,
    getBaseTextStyle,
    getListTextPreservePadding,
    TextMeasurer,
} from '@/utils/text-kit';
import {
    TextAlignType,
    TextWritingType,
    VectorTextInfo,
    type ILayerProps,
    type IVectorLayerProps,
} from '@/version-types';
import { TextEditDependency } from '../dependency';
import { getTextEditState } from '../states/text-edit-state';
import { type AbilityDependency, type ClipboardData } from '../types';
import { cleanAndUnescapeHtml, insertPlainText } from '../utils';
import { type ITextEditAdapter } from './interface';

export class VectorLayerAdapter implements ITextEditAdapter {
    onBeforeEnterEditing(_: IFramework, originLayer: ILayerProps) {
        const layerData = originLayer.data as IVectorLayerProps;

        if (layerData.textInfo) return originLayer;

        const layerSize = {
            width: originLayer.width,
            height: originLayer.height,
        };
        const textInfo = createDefaultVectorLayerTextInfo(layerSize);

        if (layerData.fill) {
            const isTransparent = hexToRgba(layerData.fill).a === 0;

            if (isTransparent) {
                textInfo.color = '#000000ff';
            } else {
                const isDark = checkIsDarkColor(layerData.fill);
                textInfo.color = isDark ? '#ffffffff' : '#000000ff';
            }
        }

        const nextLayerData: IVectorLayerProps = { ...layerData, textInfo };
        const nextLayer = { ...originLayer, data: nextLayerData, __isOnTextEditing: true };

        return nextLayer;
    }

    onAfterEnterEditing(framework: IFramework, originLayer: ILayerProps) {
        const nextLayer = {
            ...originLayer,
            __isOnTextEditing: true,
        };

        framework.stage.addOrUpdate(nextLayer);

        return nextLayer;
    }

    private sendLayerDiff(framework: IFramework, layer: ILayerProps) {
        framework.history.realEdit.sendDiff({
            [layer.id]: {
                data: { textInfo: (layer.data as IVectorLayerProps).textInfo },
                materialIds: { 'font-family': layer.materialIds?.['font-family'] },
            },
        });
    }

    async onExitEditing(framework: IFramework, originLayer: ILayerProps, latestLayer: ILayerProps | null) {
        if (!latestLayer) return;

        const originLayerData = originLayer.data as IVectorLayerProps;
        const latestLayerData = latestLayer.data as IVectorLayerProps;
        const hasChanged = JSON.stringify(latestLayerData) !== JSON.stringify(originLayerData);

        const nextLayer = { ...latestLayer, __isOnTextEditing: false };
        framework.stage.addOrUpdate(nextLayer);

        if (hasChanged) {
            framework.history.write(omit(originLayer, '__isOnTextEditing'), omit(nextLayer, '__isOnTextEditing'));
        }
        // 对比编辑状态下的是否收到最新文本
        if (originLayer.__renderTextVersion !== nextLayer.__renderTextVersion) {
            this.sendLayerDiff(framework, nextLayer);
        }

        return latestLayer;
    }

    onUpdate(framework: IFramework, editArea: HTMLDivElement, layer: ILayerProps) {
        const text = cleanAndUnescapeHtml(editArea.innerHTML);

        const nextLayer = cloneDeep(layer);
        const nextLayerData = nextLayer.data as IVectorLayerProps;
        const nextTextInfo = nextLayerData.textInfo as VectorTextInfo;

        // step1. 设置 `modified` 标记
        let layerExtraInfo = get(layer, 'extraInfo', {}) as Record<string, any>;

        // 存在 extraInfo 为空字符串的情况，需要将 extraInfo 转换为对象
        if (Object.prototype.toString.call(layerExtraInfo) !== '[object Object]') {
            console.log('VectorLayerAdapter.onUpdate: extraInfo is not an object', layer);
            layerExtraInfo = {};
        }

        layerExtraInfo.modified = true;
        nextLayer.extraInfo = layerExtraInfo;

        // step2. 设置 `textInfo.text`
        nextTextInfo.text = text;

        // step3. 根据输入内容设置文本图层 layout
        const { writingMode } = nextTextInfo;
        const isHorizontal = writingMode === TextWritingType.HORIZONTAL_TB;

        const measureOptions: Parameters<typeof TextMeasurer.measure>[1] = {
            keepConstantWidth: isHorizontal,
            keepConstantHeight: !isHorizontal,
        };

        if (nextTextInfo.padding) {
            measureOptions.preservePadding = nextTextInfo.padding;
        }

        let textSize = TextMeasurer.measure(nextLayer, measureOptions);

        // HACK: 一次性复制长文本时，需要重新以舞台宽度为图层宽度进行尺寸计算
        const stageIdx = framework.stage.getActiveStageIndex();
        const stageWidth = framework.getSizeByIndex(stageIdx).width;
        const willOverflow = textSize.width + nextLayer.left > stageWidth;

        if (willOverflow) {
            textSize = TextMeasurer.measure({ ...nextLayer, width: stageWidth }, measureOptions);
        }

        // step4.1 设置文本图层尺寸
        const proactiveWidth = layer.__proactiveWidth || layer.width;
        const proactiveHeight = layer.__proactiveHeight || layer.height;

        if (isHorizontal) {
            nextLayer.height = textSize.height < proactiveHeight ? proactiveHeight : textSize.height;
        } else {
            nextLayer.width = textSize.width < proactiveWidth ? proactiveWidth : textSize.width;
        }

        // step4.2 设置文本图层定位
        const { textAlign } = nextTextInfo;
        if (textAlign === TextAlignType.CENTER) {
            nextLayer.left = layer.left + (layer.width - nextLayer.width) / 2;
        } else if (textAlign === TextAlignType.RIGHT) {
            nextLayer.left = layer.left + (layer.width - nextLayer.width);
        }

        // step5. 更新文本图层
        framework.stage.addOrUpdate(nextLayer);
        framework.operate.setActiveLayer(nextLayer.id);

        return layer;
    }

    onCopyOrCut(framework: IFramework, layer: ILayerProps) {
        const layerData = layer.data as IVectorLayerProps;
        const textInfo = layerData.textInfo;

        if (!textInfo || !textInfo.text) return;

        framework.getInstance(TextEditDependency).setPlainTextToClipboard(textInfo.text);
    }

    onPaste(framework: IFramework, layer: ILayerProps, data: ClipboardData) {
        const { text } = data;
        insertPlainText(text);
    }

    onBlur(framework: IFramework, layer: ILayerProps) {
        const originLayer = getTextEditState(framework.identity).getOriginLayer();

        if (!originLayer) return;

        const originText = get(originLayer, 'data.textInfo.text');
        const currentText = get(layer, 'data.textInfo.text');

        if (currentText !== originText) {
            framework.history.write(omit(originLayer, '__isOnTextEditing'), omit(layer, '__isOnTextEditing'));
        }
        // 对比编辑状态下的是否收到最新文本
        if (originLayer.__renderTextVersion !== layer.__renderTextVersion) {
            this.sendLayerDiff(framework, layer);
        }
    }

    getEditAreaStyle(_: IFramework, layer: ILayerProps) {
        const layerData = layer.data as IVectorLayerProps;
        const textInfo = layerData.textInfo;

        if (!textInfo) return {};

        const baseTextInfo = extractBaseTextInfoFromVectorLayer(layer);
        const baseStyle = getBaseTextStyle(baseTextInfo);

        const preservePadding = {
            paddingTop: 0,
            paddingRight: 0,
            paddingBottom: 0,
            paddingLeft: 0,
        };

        if (baseTextInfo.listStyle) {
            const listTextPreservePadding = getListTextPreservePadding(baseTextInfo);
            preservePadding.paddingTop += listTextPreservePadding.paddingTop;
            preservePadding.paddingLeft += listTextPreservePadding.paddingLeft;
        }

        if (textInfo.padding) {
            const { top, right, bottom, left } = textInfo.padding;
            preservePadding.paddingTop += top;
            preservePadding.paddingRight += right;
            preservePadding.paddingBottom += bottom;
            preservePadding.paddingLeft += left;
        }

        const editAreaStyles: React.CSSProperties = {
            ...baseStyle,
            ...preservePadding,
            color: 'transparent', // 矢量图层在编辑态时，需要将交互层的文字隐藏，直接显示渲染层，防止文字重叠、特效无效正确展示的问题
            caretColor: baseStyle.color,
        };

        // 文字编辑层去除 text-size-adjust
        delete editAreaStyles.WebkitTextSizeAdjust;

        return editAreaStyles;
    }

    getEditAreaTextContent(_: IFramework, layer: ILayerProps) {
        const layerData = layer.data as IVectorLayerProps;
        const textInfo = layerData.textInfo;

        return {
            uniformText: textInfo ? textInfo.text : '',
        };
    }

    getEditAreaWrapperStyle() {
        return null;
    }

    useRendererDisplay() {
        return true;
    }

    getEnterEditingModeWays(): Array<'click' | 'doubleClick'> {
        return ['doubleClick'];
    }
}
